import { AxiosRequestConfig } from 'axios'

export type AxiosRequestMethod<TData, TResponse> =
  (data: TData, _config?: AxiosRequestConfig) => Promise<TResponse>

export type CancellableAxiosRequestMethod<TData, TResponse> =
  (data: TData, _config?: Omit<AxiosRequestConfig, 'cancelToken'>) => Promise<TResponse>

/**
 * Wraps the given `requestMethod` so that only a single instance of the request can be executing at any time.
 * Preemptive - when a new request is started, the ongoing request is cancelled (if there is one).
 * @param requestMethod
 */
export const createPreemptiveRequest =
  <TData, TResponse>(requestMethod: AxiosRequestMethod<TData, TResponse>): CancellableAxiosRequestMethod<TData, TResponse> => {
    let previousAbortController: AbortController | null = null

    return async (data: TData, _config?: Omit<AxiosRequestConfig, 'signal' | 'cancelToken'>): Promise<TResponse> => {
      if (previousAbortController !== null) {
        previousAbortController.abort('Cancelled by the next request')
        previousAbortController = null
      }

      let currentAbortController: AbortController | null = null

      try {
        currentAbortController = new AbortController()
        previousAbortController = currentAbortController

        return await requestMethod(data, {..._config, signal: currentAbortController.signal})
      } finally {
        // Cleanup only if it wasn't cancelled
        if (previousAbortController === currentAbortController) {
          previousAbortController = null
        }
      }
    }
  }